import React from 'react';
import { Box } from '@drone-js/core';
import { LazyAddToCartToastWrapper, LazyCartFloatingButton } from 'core/utils/lazyCart';
import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { OverlayType, showOverlay } from 'store/slice/overlay';
import { connect } from 'react-redux';
import { selectBehaviorFromBootstrap, selectIsWebViewFromBootstrap } from 'store/slice/bootstrap';
import { selectIsRebrandCartToSaved } from 'core/utils/cart';
import { selectScreenSize } from 'store/slice/screen';

interface CartFloatingButtonProps {
    position?: 'fixed' | 'static';
}
interface DispatchProps {
    openOverlay: () => void;
}

interface StateProps {
    isRebrandCartToSaved: boolean;
    isTabletOrDesktop: boolean;
}

type Props = CartFloatingButtonProps & DispatchProps & StateProps;

const CartFloatingContainerComponent: React.FC<Props> = React.memo((props: Props) => {
    const { openOverlay, position, isRebrandCartToSaved } = props;

    if (!isRebrandCartToSaved) {
        return null;
    }

    return (
        <React.Suspense fallback={null}>
            <Box
                data-testid="cart-floating-container"
                data-element-name="cart-floating-container"
                position={position}
                bottom="20px"
                zIndex={100}
                end="24px"
                display="flex"
                flexDirection="column"
                alignItems="flex-end"
                gap="8"
                me={position === 'static' ? '24' : undefined}
                mb={position === 'static' ? '16' : undefined}
            >
                <LazyAddToCartToastWrapper />
                {props.isTabletOrDesktop && <LazyCartFloatingButton onClick={openOverlay} />}
            </Box>
        </React.Suspense>
    );
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    openOverlay: () => {
        dispatch(showOverlay(OverlayType.CartSummary));
    },
});

export const mapStateToProps = createSelector(
    selectBehaviorFromBootstrap,
    selectIsWebViewFromBootstrap,
    selectScreenSize,

    (behavior, isWebView, screenSize): StateProps => {
        const isRebrandCartToSaved = selectIsRebrandCartToSaved(behavior, isWebView);
        const isTabletOrDesktop = screenSize.tabletOrDesktop;
        return {
            isRebrandCartToSaved,
            isTabletOrDesktop,
        };
    },
);

CartFloatingContainerComponent.displayName = 'CartFloatingButtonComponent';

CartFloatingContainerComponent.defaultProps = {
    position: 'fixed',
};

const CartFloatingContainer = connect(mapStateToProps, mapDispatchToProps)(CartFloatingContainerComponent);

export { CartFloatingContainer, CartFloatingContainerComponent };
